import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  CircleSlash,
  Clock,
  Loader2,
  RefreshCw,
  XCircle,
} from "lucide-react";
import {
  Badge,
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@eduviaos/ui-kit";
import type {
  NotificationDispatchAttemptDTO,
  NotificationDispatchOutcomeValue,
  NotificationTrigger,
  NotificationTriggerLog,
} from "@eduviaos/shared";
import { useNotificationTriggerLogs } from "@/hooks/useNotificationTriggers";

/**
 * Phase F0b — Dispatch history sheet for one NotificationTrigger.
 *
 * Reads `GET /notifications/triggers/{id}/logs?limit=50` and renders the
 * resulting `NotificationLog` rows newest-first. Each log row carries:
 *   - `event` (echo of the event that fired)
 *   - `correlation_id` (request id propagated from the originating call —
 *     surfaced as a copy-on-click chip so on-call can grep across services)
 *   - `payload_summary` (small extract of the event payload — enquiry_id,
 *     stage_key, etc.)
 *   - `attempts[]` (per-recipient × per-channel rows with outcome,
 *     duration, error_code, error_message, provider_message_id)
 *
 * The dispatcher writes one log row per trigger-fire (NOT per recipient).
 * A trigger with 5 recipients × 2 channels = 1 log row with 10 attempt
 * children — making the audit experience grep-friendly without joining
 * tables.
 */

interface NotificationLogsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: NotificationTrigger | null;
}

const OUTCOME_APPEARANCE: Record<
  NotificationDispatchOutcomeValue,
  { label: string; icon: React.ComponentType<{ className?: string }>; className: string }
> = {
  sent: {
    label: "Sent",
    icon: CheckCircle2,
    className: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
  },
  deferred: {
    label: "Deferred",
    icon: Clock,
    className: "border-amber-500/30 bg-amber-500/5 text-amber-800 dark:text-amber-300",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    className: "border-destructive/30 bg-destructive/5 text-destructive",
  },
  dropped: {
    label: "Dropped",
    icon: CircleSlash,
    className: "border-border/60 bg-muted text-muted-foreground",
  },
};

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function NotificationLogsSheet({ open, onOpenChange, trigger }: NotificationLogsSheetProps) {
  const triggerId = trigger?.id ?? null;
  const logsQuery = useNotificationTriggerLogs(triggerId);

  const items = logsQuery.data?.items ?? [];
  const total = logsQuery.data?.total ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-border/60 border-b p-6">
          <SheetTitle>Dispatch history{trigger ? ` — ${trigger.name}` : ""}</SheetTitle>
          <SheetDescription>
            {trigger ? (
              <>
                Append-only audit log for the{" "}
                <code className="bg-muted rounded px-1 py-0.5 font-mono text-[11px]">
                  {trigger.event}
                </code>{" "}
                trigger. One row per trigger-fire — per-recipient attempts are nested below each
                row.
              </>
            ) : (
              "Open the audit log from the actions menu on a trigger to see its dispatch history."
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <span className="text-muted-foreground text-xs tabular-nums">
              {total.toLocaleString()} {total === 1 ? "fire" : "fires"} (most recent first)
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void logsQuery.refetch()}
              disabled={logsQuery.isFetching}
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-3.5", logsQuery.isFetching && "animate-spin")}
              />
              Refresh
            </Button>
          </div>

          {logsQuery.isLoading ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-12 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              Loading history…
            </div>
          ) : null}

          {logsQuery.isError ? (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">
                Couldn't load the dispatch history. Try again — if it keeps failing, check the
                network.
              </span>
            </div>
          ) : null}

          {!logsQuery.isLoading && !logsQuery.isError && items.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed px-4 py-10 text-center text-sm">
              No dispatches yet. The first fire shows up here within a few seconds of the matching
              event hitting the queue.
            </div>
          ) : null}

          <ul className="space-y-3">
            {items.map((log) => (
              <LogRow key={log.id} log={log} />
            ))}
          </ul>
        </div>

        <div className="border-border/60 flex items-center justify-end gap-2 border-t p-4">
          <SheetClose asChild>
            <Button variant="outline" size="sm">
              Close
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function LogRow({ log }: { log: NotificationTriggerLog }) {
  const [open, setOpen] = React.useState(false);
  const summary = aggregateAttempts(log.attempts);

  return (
    <li className="border-border/60 rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="hover:bg-muted/40 flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-foreground text-sm font-medium tabular-nums">
              {formatTimestamp(log.created_at)}
            </span>
            <code className="text-muted-foreground bg-muted rounded px-1 py-0.5 font-mono text-[10.5px]">
              {log.event}
            </code>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {summary.sent > 0 ? (
              <Badge
                variant="outline"
                className="border-emerald-500/30 bg-emerald-500/5 text-[10.5px] text-emerald-700 dark:text-emerald-300"
              >
                {summary.sent} sent
              </Badge>
            ) : null}
            {summary.deferred > 0 ? (
              <Badge
                variant="outline"
                className="border-amber-500/30 bg-amber-500/5 text-[10.5px] text-amber-800 dark:text-amber-300"
              >
                {summary.deferred} deferred
              </Badge>
            ) : null}
            {summary.failed > 0 ? (
              <Badge
                variant="outline"
                className="border-destructive/30 bg-destructive/5 text-destructive text-[10.5px]"
              >
                {summary.failed} failed
              </Badge>
            ) : null}
            {summary.dropped > 0 ? (
              <Badge
                variant="outline"
                className="border-border/60 text-muted-foreground text-[10.5px]"
              >
                {summary.dropped} dropped
              </Badge>
            ) : null}
            {log.correlation_id ? (
              <span
                className="text-muted-foreground/80 font-mono text-[10.5px]"
                title="Correlation id (propagated from the originating request)"
              >
                cid: {log.correlation_id.slice(0, 8)}…
              </span>
            ) : null}
          </div>
        </div>
        <span className="text-muted-foreground/70 mt-1 text-[11px]">{open ? "−" : "+"}</span>
      </button>
      {open ? (
        <div className="border-border/60 space-y-3 border-t px-3 py-3">
          {Object.keys(log.payload_summary).length > 0 ? (
            <div>
              <p className="text-muted-foreground mb-1 text-[11px] uppercase tracking-wide">
                Payload summary
              </p>
              <pre className="bg-muted/40 max-h-32 overflow-auto rounded px-2 py-1.5 font-mono text-[11px] leading-snug">
                {JSON.stringify(log.payload_summary, null, 2)}
              </pre>
            </div>
          ) : null}
          <div>
            <p className="text-muted-foreground mb-1.5 text-[11px] uppercase tracking-wide">
              Attempts ({log.attempts.length})
            </p>
            <ul className="space-y-1.5">
              {log.attempts.map((a, idx) => (
                <AttemptRow key={`${log.id}-${idx}`} attempt={a} />
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function AttemptRow({ attempt }: { attempt: NotificationDispatchAttemptDTO }) {
  const ap = OUTCOME_APPEARANCE[attempt.outcome];
  const Icon = ap.icon;
  return (
    <li
      className={cn("flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs", ap.className)}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium">{ap.label}</span>
          <span className="text-[11px] opacity-70">via {attempt.channel}</span>
          <span className="text-[11px] tabular-nums opacity-70">{attempt.duration_ms}ms</span>
        </div>
        <p className="break-all font-mono text-[10.5px] opacity-90">→ {attempt.recipient}</p>
        {attempt.error_code || attempt.error_message ? (
          <p className="text-[11px] opacity-90">
            {attempt.error_code ? <strong>{attempt.error_code}: </strong> : null}
            {attempt.error_message}
          </p>
        ) : null}
        {attempt.provider_message_id ? (
          <p className="font-mono text-[10.5px] opacity-70">
            provider_id: {attempt.provider_message_id}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function aggregateAttempts(attempts: NotificationDispatchAttemptDTO[]) {
  const out = { sent: 0, deferred: 0, failed: 0, dropped: 0 };
  for (const a of attempts) {
    out[a.outcome] += 1;
  }
  return out;
}
